<template>
    <div class="billing-date-filter">
        <label class="uk-form-label">
            {{ labelText }}
            <span class="info-icon"
                  :uk-tooltip="`title: ${tooltipText}; pos: top-left`">
                <i class="material-icons">info</i>
            </span>
        </label>
        <div class="uk-flex uk-flex-middle">
            <input class="uk-input" type="text" maxlength="8"
                   :name="fromName"
                   :placeholder="placeholder"
                   :value="fromValue">
            <span style="margin: 0 6px">~</span>
            <input class="uk-input" type="text" maxlength="8"
                   :name="toName"
                   :placeholder="placeholder"
                   :value="toValue">
            <slot name="presets"></slot>
        </div>
    </div>
</template>

<script>
    /**
     * TASK-043 — 과금기준일 검색 필터
     *
     * 1-B 결정 2 (Vue 동적 라벨) + 결정 3 (인포 아이콘 + 호버 툴팁) 채택.
     *
     * REF-020 hot-fix #20 안건 85 (2026-05-14):
     *   - 라벨: 과금타입에 따라 동적 변경 ("과금기준일" / "과금기준일(출고일)" / "과금기준일(청구일)")
     *   - 툴팁: 모든 과금타입에 대한 의미 설명을 한 번에 노출 (고정 텍스트)
     *
     * 정의 출처: doc/명세/과금기준일-정의.md §4
     *
     * @example
     *   <billing-date-filter
     *       :billing-type="$billingType"
     *       from-name="billing_date_from"
     *       to-name="billing_date_to"
     *       :from-value="$fromValue"
     *       :to-value="$toValue">
     *       <template slot="presets">
     *           @include('components.billing-date-presets', [...])
     *       </template>
     *   </billing-date-filter>
     */
    export default {
        props: {
            /** 현재 선택된 과금타입 (BillingType 코드, 빈 문자열=전체) — 초기값 (URL query string) */
            billingType: {
                type: [String, Number],
                default: '',
            },
            /** from input name 속성 */
            fromName: {
                type: String,
                default: 'billing_date_from',
            },
            /** to input name 속성 */
            toName: {
                type: String,
                default: 'billing_date_to',
            },
            /** from 초기값 */
            fromValue: {
                type: String,
                default: '',
            },
            /** to 초기값 */
            toValue: {
                type: String,
                default: '',
            },
            /** input placeholder */
            placeholder: {
                type: String,
                default: '20260101',
            },
        },
        /**
         * 안건 87 (2026-05-14): 폼 서브밋 전에도 select[name="billing_type"] 변경 즉시 라벨 갱신.
         * data 의 currentBillingType 이 reactive — labelText / tooltipText 가 이를 참조.
         */
        data() {
            return {
                currentBillingType: String(this.billingType || ''),
            };
        },
        mounted() {
            // 같은 form 안의 billing_type select 를 찾아 change 리스너 등록
            const form = this.$el.closest('form');
            const select = form ? form.querySelector('select[name="billing_type"]') : null;
            if (select) {
                this._billingTypeListener = (e) => {
                    this.currentBillingType = String(e.target.value || '');
                };
                select.addEventListener('change', this._billingTypeListener);
            }
        },
        beforeDestroy() {
            const form = this.$el && this.$el.closest ? this.$el.closest('form') : null;
            const select = form ? form.querySelector('select[name="billing_type"]') : null;
            if (select && this._billingTypeListener) {
                select.removeEventListener('change', this._billingTypeListener);
            }
        },
        computed: {
            /**
             * 과금타입별 라벨 텍스트 — 안건 85 (2026-05-14) 동적 부기.
             * 안건 87 (2026-05-14): currentBillingType data 참조 (select 변경 즉시 반영).
             *
             * - 전체 (빈 값): "과금기준일"
             * - 출고운임 (21): "과금기준일(출고일)"
             * - 그 외 (11/12/23/24/31/32/41): "과금기준일(청구일)"
             */
            labelText() {
                const key = this.currentBillingType;
                if (key === '') {
                    return '과금기준일';
                }
                if (key === '21') {
                    return '과금기준일(출고일)';
                }
                return '과금기준일(청구일)';
            },

            /**
             * 인포 툴팁 — 모든 과금타입의 과금기준일 정의 목록 (고정).
             * 안건 87 (2026-05-14): 각 과금타입별 한 줄씩 명시적 목록으로 확장.
             */
            tooltipText() {
                return [
                    '과금기준일은 과금타입별로 의미가 다릅니다.',
                    '',
                    '• 출고운임 → 출고일',
                    '• 추가비용 → 청구일',
                    '• 물품가비용 → 청구일',
                    '• 통관발생비용 → 청구일',
                    '• 기타운임 → 청구일',
                    '• 반품비용 → 청구일',
                    '• 배송지비용 → 청구일',
                    '• 수기과금 → 청구일',
                ].join('\n');
            },
        },
    };
</script>

<style lang="scss" scoped>
    .billing-date-filter {
        .info-icon {
            display: inline-flex;
            align-items: center;
            color: #888;
            cursor: help;
            margin-left: 4px;

            i.material-icons {
                font-size: 14px;
            }

            &:hover {
                color: #1e87f0;
            }
        }
    }
</style>
